import React from 'react';
import { formatBusinessMonthLatn, formatDateLatn } from '../../../utils/dateFormatter';
import { arabicPlural } from '../../../utils/alertDisplay';
import { formatNumber } from './reportFormat';
// The MARK, not the full lockup. `Hostme1.png` is an 8334×8334 square (~70 megapixels,
// 415KB) whose wordmark occupies a third of the box — it decoded too slowly to make it
// into a print, and rendered small when it did. This asset is 1402×1118 / 49KB, and the
// company name beside it is real text: it always renders, at any size, in the document's
// own typeface. (Copied from web/public/images so it is imported and content-hashed like
// every other src asset, rather than referenced by a public URL containing spaces.)
import hostMeMark from '../../../assets/images/branding/hostme-mark.png';

/**
 * The branded head of the printed owner report.
 *
 * ── Why a masthead and not a cover page ─────────────────────────────────────
 * A separate cover sheet would add a page to a document that is often only three
 * or four, and an owner opening a PDF wants the month and the headline figures,
 * not a title card. A masthead brands page 1 and puts the first metrics on it.
 *
 * Chrome cannot repeat this on later pages — it ignores the CSS margin boxes that
 * would draw a running header — so page 1 is branded and continuation pages are
 * plain. That is a known, accepted limit of printing from a browser, recorded in
 * tasks/owner-monthly-report.md §19.4, not an oversight.
 *
 * ── Numerals ────────────────────────────────────────────────────────────────
 * Dates use the `*Latn` formatters so the whole document is in one numeral system.
 * The printed sample showed "3 عقارات" beside "٣٠ يوليو ٢٠٢٦" on the same line,
 * which reads as two documents stapled together.
 *
 * Screen-only header stays in MonthlyReportView; this replaces it when printing.
 */
const ReportMasthead = ({ periodMonth, ownerName, propertyCount, generatedAt }) => (
  <header className="report-masthead">
    <div className="report-masthead-brand">
      <div className="report-masthead-identity">
        <img src={hostMeMark} alt="" className="report-masthead-logo" />
        <span className="report-masthead-wordmark">Host Me</span>
      </div>
      <span className="report-masthead-kind">تقرير المالك</span>
    </div>

    <p className="report-masthead-title">تقرير الأداء الشهري</p>
    <h1 className="report-masthead-period">{formatBusinessMonthLatn(periodMonth)}</h1>

    {/* Naming the owner is what makes this a document addressed to someone, rather
        than a screenshot of a dashboard. Omitted rather than faked when the field
        is absent — a frontend deployed ahead of the backend will not receive it. */}
    {ownerName && (
      <p className="report-masthead-for">
        <span>أُعدّ لـ</span>
        {ownerName}
      </p>
    )}

    <hr className="report-masthead-rule" />

    <div className="report-masthead-meta">
      <span>
        {formatNumber(propertyCount || 0)}{' '}
        {arabicPlural(propertyCount || 0, 'عقار', 'عقاران', 'عقارات')}
      </span>
      <span>أُنشئ في {formatDateLatn(generatedAt)}</span>
    </div>
  </header>
);

export default ReportMasthead;
